import axios from 'axios';
import { loadAllEffectiveLeads } from '../utils/effectiveLeadsLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
console.log('🔗 API Base URL:', API_BASE_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 6400000, // 120 minutes - much longer timeout for batch operations
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    // These adapters preserve a safe error envelope themselves. Do not log
    // response rows, provider payloads or credentials, or discard HTTP details.
    if (error.config?.url === '/leads/effective'
      || error.config?.url === '/batchdata/v3/leads/jobs'
      || error.config?.url === '/batchdata/v3/leads/recovery-review'
      || /\/batchdata\/v3\/leads\/jobs\/[^/]+$/.test(error.config?.url || '')
      || /\/batchdata\/v3\/leads\/jobs\/[^/]+\/(confirm-uncertain-retry|reconcile-uncertain|resume-dispatch)$/.test(error.config?.url || '')) return Promise.reject(error);
    console.error('API Response Error:', error.response?.status, error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 429) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please try again.');
    }
    
    throw error;
  }
);

function recoveryError(error, fallback) {
  const data = error.response?.data;
  const wrapped = new Error(typeof data?.error === 'string' && data.error.trim() ? data.error : fallback);
  wrapped.status = error.response?.status;
  if (typeof data?.code === 'string') wrapped.code = data.code;
  return wrapped;
}

export const scrapingService = {
  // Geo helpers
  async fetchStates() {
    try {
      const response = await api.get('/geo/states');
      return response.data;
    } catch (error) {
      console.error('Error fetching states:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch states');
    }
  },

  async fetchCounties(stateId) {
    try {
      const response = await api.get(`/geo/counties?stateId=${encodeURIComponent(stateId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching counties:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch counties');
    }
  },

  async fetchAcreageRange(countyId) {
    try {
      const response = await api.get(`/geo/acreage-range?countyId=${encodeURIComponent(countyId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching acreage range:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch acreage range');
    }
  },
  /**
   * Start a new scraping job
   * @param {Object} jobData - The scraping job configuration
   * @param {string[]} jobData.queries - Array of search queries
   * @param {string} jobData.businessType - Type of business to search for
   * @param {string[]} jobData.regions - Array of regions to search in
   * @param {number} jobData.maxResults - Maximum results per query
   * @param {number} jobData.waitTime - Wait time between requests
   * @returns {Promise<Object>} Job information
   */
  async startScraping(jobData) {
    try {
      const response = await api.post('/scraping/start', jobData);
      return response.data;
    } catch (error) {
      console.error('Error starting scraping job:', error);
      throw new Error(error.response?.data?.error || 'Failed to start scraping job');
    }
  },

  // Function to process raw data for Tracerfy (Newly added)
  async processRawDataForTracerfy(rawData, options = {}) {
    try {
      const response = await api.post('/tracerfy/process', {
        rawData: rawData?.rows || [],
        options: { 
          testMode: true, 
          sampleSize: 10,
          ...options 
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error processing raw data for Tracerfy:', error);
      throw error.response?.data?.error || error.message || 'Failed to process data';
    }
  },

  /**
   * Stop a running scraping job
   * @param {string} jobId - The job ID to stop
   * @returns {Promise<Object>} Success confirmation
   */
  async stopScraping(jobId) {
    try {
      const response = await api.post(`/scraping/stop/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error stopping scraping job:', error);
      throw new Error(error.response?.data?.error || 'Failed to stop scraping job');
    }
  },

  /**
   * Get the status of a specific scraping job
   * @param {string} jobId - The job ID to check
   * @returns {Promise<Object>} Job status information
   */
  async getJobStatus(jobId) {
    try {
      const response = await api.get(`/scraping/status/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      throw new Error(error.response?.data?.error || 'Failed to get job status');
    }
  },

  /**
   * Get all active scraping jobs
   * @returns {Promise<Object[]>} Array of active jobs
   */
  async getActiveJobs() {
    try {
      const response = await api.get('/scraping/jobs');
      return response.data;
    } catch (error) {
      console.error('Error getting active jobs:', error);
      throw new Error(error.response?.data?.error || 'Failed to get active jobs');
    }
  },

  /**
   * Check API health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error checking API health:', error);
      throw new Error('API health check failed');
    }
  },

  /**
   * Fetch all non-enriched data from the database
   * @param {number} limit - Maximum number of records to fetch (default: 1000)
   * @param {number} offset - Number of records to skip for pagination (default: 0)
   * @returns {Promise<Object>} { table, count, rows, pagination }
   */
  async fetchAllNonEnrichedData(limit = 1000, offset = 0) {
    try {
      const response = await api.get(`/supabase/non-enriched/all?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all non-enriched data:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch non-enriched data');
    }
  },

  /**
   * Fetch filtered non-enriched data based on county and acreage
   * @param {number} countyId - County ID to filter by
   * @param {number} minAcres - Minimum acres (default: 0)
   * @param {number} maxAcres - Maximum acres (optional)
   * @param {number} limit - Records per page (default: 100)
   * @param {number} offset - Number of records to skip (default: 0)
   * @returns {Promise<Object>} { table, county_id, min_acres, max_acres, count, rows, pagination }
   */
  async fetchFilteredNonEnrichedData(countyId, minAcres = 0, maxAcres = null, limit = 100, offset = 0) {
    try {
      const params = new URLSearchParams({
        countyId: countyId.toString(),
        minAcres: minAcres.toString(),
        limit: limit.toString(),
        offset: offset.toString()
      });
      
      if (maxAcres) {
        params.append('maxAcres', maxAcres.toString());
      }
      
      const response = await api.get(`/supabase/non-enriched/filtered?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered non-enriched data:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch filtered non-enriched data');
    }
  },

  // Unique owner data
  async fetchUniqueOwnerNonEnriched(countyId, limit = 50, offset = 0) {
    try {
      const params = new URLSearchParams({ countyId: String(countyId), limit: String(limit), offset: String(offset) });
      const response = await api.get(`/supabase/unique-owner/non-enriched?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unique owner non-enriched:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch unique owner non-enriched');
    }
  },

  // ALL rows in one go (server batches internally) (New function)
  // async fetchUniqueOwnerNonEnrichedAll(countyId) {
  //   try {
  //     const params = new URLSearchParams({ countyId: String(countyId), all: 'true' });
  //     const response = await api.get(`/supabase/unique-owner/non-enriched?${params}`);
  //     return response.data; // { rows: [...] raw objects }
  //   } catch (error) {
  //     console.error('Error fetching ALL unique owner non-enriched:', error);
  //     throw new Error(error.response?.data?.error || 'Failed to fetch ALL unique owner non-enriched');
  //   }
  // },

  // ALL rows in one go with acreage filtering (server batches internally) (New function)
  // async fetchUniqueOwnerNonEnrichedAll(countyId, minAcres, maxAcres, filterMode = 'between') {
  //   try {
  //     const params = { countyId, minAcres, filterMode };
  //     if (maxAcres) params.maxAcres = maxAcres;
      
  //     const response = await api.get('/supabase/unique-owner-non-enriched/all', { params });
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error fetching all unique owner non-enriched data:', error);
  //     throw new Error(error.response?.data?.error || 'Failed to fetch data');
  //   }
  // },

    async fetchUniqueOwnerNonEnrichedAll(countyId, minAcres = 50, maxAcres = null, filterMode = 'between') {
    try {
      const params = new URLSearchParams({ 
        countyId: String(countyId),
        all: 'true',
        minAcres: String(minAcres),
        filterMode: filterMode
      });
      
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/non-enriched?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ALL unique owner non-enriched:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch ALL non-enriched records');
    }
  },

  async fetchUniqueOwnerEnriched(countyId, limit = 50, offset = 0) {
    try {
      const params = new URLSearchParams({ countyId: String(countyId), limit: String(limit), offset: String(offset) });
      const response = await api.get(`/supabase/unique-owner/enriched?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unique owner enriched:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch unique owner enriched');
    }
  },

  async traceUniqueOwners(uniqueIds, countyId) {
    try {
      const response = await api.post('/trace/unique-owners', { uniqueIds, countyId });
      return response.data;
    } catch (error) {
      console.error('Error tracing unique owners:', error);
      throw new Error(error.response?.data?.error || 'Failed to trace unique owners');
    }
  },

  /**
   * Fetch a batch of leads for review
   * @param {number} batch - Batch number (1-based)
   * @param {number} size - Batch size
   * @returns {Promise<Object>} { leads, batch, size, total }
   */
  async fetchLeadsBatch(batch = 1, size = 10) {
    try {
      const response = await api.get(`/leads?batch=${batch}&size=${size}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching leads batch:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch leads batch');
    }
  },

  /**
   * Mark leads as contacted manually
   * @param {string[]} leadIds
   * @returns {Promise<Object>} Success confirmation
   */
  async markLeadsContacted(leadIds) {
    try {
      const response = await api.post('/leads/contact', { leadIds });
      return response.data;
    } catch (error) {
      console.error('Error marking leads as contacted:', error);
      throw new Error(error.response?.data?.error || 'Failed to mark leads as contacted');
    }
  },

  /**
   * Add leads to Smartlead campaign for automated outreach
   * @param {string[]} leadIds
   * @param {string} campaignId - Smartlead campaign ID
   * @returns {Promise<Object>} Success confirmation
   */
  async automateLeads(leadIds, campaignId, county) {
    try {
      const response = await api.post('/leads/automate', { leadIds, campaignId, county });
      return response.data;
    } catch (error) {
      console.error('Error automating leads:', error);
      throw new Error(error.response?.data?.error || 'Failed to automate leads');
    }
  },

  // Centralized schema support (non_enriched_data → Tracerfy → enriched_data)

  /**
   * Start tracing using centralized tables by countyId
   */
  // async traceCentralized(countyId, limit = 100, dryRun = false, minAcres = 50, maxAcres) {
  //   try {
  //     const payload = { countyId, limit, dryRun, minAcres };
  //     if (typeof maxAcres === 'number') payload.maxAcres = maxAcres;
  //     const response = await api.post('/trace/centralized', payload);

  //     console.log("✅ TRACE RESPONSE:", response.data);
  //     console.log("📊 TRACE METRICS:", { 
  //       count: response.data?.count || 0,
  //       queueId: response.data?.queue_id,
  //       cost: response.data?.estimated_cost
  //     });

  //     return response.data;
  //   } catch (error) {
  //     console.error('Error starting centralized trace:', error);
  //     throw new Error(error.response?.data?.error || 'Failed to start centralized trace');
  //   }
  // },

    async traceCentralized(countyId, minAcres = 50, maxAcres, filterMode = 'between', dryRun = false) {
      try {
        const payload = { countyId, minAcres, filterMode, dryRun };
        if (typeof maxAcres === 'number') payload.maxAcres = maxAcres;
        
        const response = await api.post('/trace/centralized', payload);

        console.log("✅ TRACE RESPONSE:", response.data);
        console.log("📊 TRACE METRICS:", { 
          count: response.data?.count || 0,
          queueId: response.data?.queue_id,
          cost: response.data?.estimated_cost
        });

        return response.data;
      } catch (error) {
        console.error('Error starting centralized trace:', error);
        throw new Error(error.response?.data?.error || 'Failed to start centralized trace');
      }
    },

  /**
   * Fetch Tracerfy queue status/results
   */
  async getTracerfyQueue(queueId) {
    try {
      const response = await api.get(`/tracerfy/queue/${encodeURIComponent(queueId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Tracerfy queue:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch Tracerfy queue');
    }
  },

  /**
   * Fetch enriched rows from centralized `enriched_data` by countyId
   */
  async fetchEnrichedByCountyId(countyId, limit = 100) {
    // Debugging output
    console.log("📡 Service: fetchEnrichedByCountyId called with:", countyId, limit);
    // End debugging output

    try {
      const response = await api.get(`/supabase/enriched/by-county/${encodeURIComponent(countyId)}?limit=${limit}`);
      // Debugging output
      console.log("📡 Service: fetchEnrichedByCountyId response:", response.data);
      // End debugging output

      return response.data;
    } catch (error) {
      console.error('Error fetching centralized enriched rows:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch enriched rows');
    }
  },

  /**
   * Automate leads from centralized table by countyId
   */
  async automateLeadsByCountyId(leadIds, campaignId, countyId, options = {}) {
    try {
      const payload = { leadIds, campaignId, countyId };
      if (options && typeof options === 'object') {
        if (typeof options.verify === 'boolean') payload.verify = options.verify;
        if (options.verificationPolicy) payload.verificationPolicy = options.verificationPolicy;
        if (options.batchSize) payload.batchSize = options.batchSize;
      }
      const response = await api.post('/leads/automate', payload);
      return response.data;
    } catch (error) {
      console.error('Error automating centralized leads:', error);
      throw new Error(error.response?.data?.error || 'Failed to automate leads');
    }
  },

  // Smartlead Campaign Management Methods

  /**
   * Fetch all Smartlead campaigns
   * @returns {Promise<Array>} List of campaigns
   */
  async fetchSmartleadCampaigns() {
    try {
      console.log('Fetching Smartlead campaigns...');
      const response = await api.get('/smartlead-campaigns');
      console.log('Campaigns fetched successfully:', response.data?.length || 0, 'campaigns');
      return response.data;
    } catch (error) {
      console.error('Error fetching Smartlead campaigns:', error);
      console.error('Error details:', error.response?.data);
      
      // Provide more specific error messages
      if (error.response?.status === 401) {
        throw new Error('Smartlead API authentication failed. Check your API key configuration.');
      } else if (error.response?.status === 429) {
        throw new Error('Smartlead API rate limit exceeded. Please wait before trying again.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Smartlead API is taking too long to respond.');
      } else {
        throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to fetch campaigns');
      }
    }
  },

  /**
   * Get specific Smartlead campaign
   * @param {string} campaignId
   * @returns {Promise<Object>} Campaign details
   */
  async getSmartleadCampaign(campaignId) {
    try {
      const response = await api.get(`/smartlead-campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Smartlead campaign:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch campaign');
    }
  },

  /**
   * Create new Smartlead campaign
   * @param {Object} campaignData - { name, subject, html_body, text_body }
   * @returns {Promise<Object>} Created campaign
   */
  async createSmartleadCampaign(campaignData) {
    try {
      console.log('Creating Smartlead campaign:', campaignData.name);
      const response = await api.post('/smartlead-campaigns', {
        ...campaignData,
        autoCreateSequence: true
      });
      console.log('Campaign created successfully:', response.data);
      if (response.data?.sequence) {
        console.log('Sequence diagnostics:', response.data.sequence);
      }
      return response.data;
    } catch (error) {
      console.error('Error creating Smartlead campaign:', error);
      console.error('Error details:', error.response?.data);
      
      // Provide more specific error messages
      if (error.response?.status === 401) {
        throw new Error('Smartlead API authentication failed. Check your API key configuration.');
      } else if (error.response?.status === 429) {
        throw new Error('Smartlead API rate limit exceeded. Please wait before trying again.');
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.details || 'Invalid campaign data provided.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Smartlead API is taking too long to respond.');
      } else {
        throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to create campaign');
      }
    }
  },

  /**
   * Ensure a campaign has at least one sequence
   */
  async ensureSmartleadSequence(campaignId, { subject, html_body, text_body, step_delay = 1 }) {
    try {
      const response = await api.post(`/smartlead-campaigns/${campaignId}/ensure-sequence`, {
        subject,
        html_body,
        text_body,
        step_delay
      });
      return response.data;
    } catch (error) {
      console.error('Error ensuring Smartlead sequence:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || 'Failed to ensure sequence');
    }
  },

  /**
   * Start/activate a campaign
   */
  async startSmartleadCampaign(campaignId) {
    try {
      const response = await api.post(`/smartlead-campaigns/${campaignId}/start`);
      return response.data;
    } catch (error) {
      console.error('Error starting Smartlead campaign:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || 'Failed to start campaign');
    }
  },

  /**
   * Update Smartlead campaign
   * @param {string} campaignId
   * @param {Object} campaignData - { name, subject, html_body, text_body }
   * @returns {Promise<Object>} Success confirmation
   */
  async updateSmartleadCampaign(campaignId, campaignData) {
    try {
      const response = await api.put(`/smartlead-campaigns/${campaignId}`, campaignData);
      return response.data;
    } catch (error) {
      console.error('Error updating Smartlead campaign:', error);
      throw new Error(error.response?.data?.error || 'Failed to update campaign');
    }
  },

  /**
   * Delete Smartlead campaign
   * @param {string} campaignId
   * @returns {Promise<Object>} Success confirmation
   */
  async deleteSmartleadCampaign(campaignId) {
    try {
      const response = await api.delete(`/smartlead-campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting Smartlead campaign:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete campaign');
    }
  },

  // County tracing (Supabase ←→ Tracerfy orchestration)

  /**
   * Start tracing a county. Creates a Tracerfy queue for first N rows from the county table
   */
  async traceCounty(county, limit = 25) {
    try {
      // Centralized-only path
      const response = await api.post('/trace/centralized', { county, limit });
      return response.data;
    } catch (error) {
      console.error('Error starting county trace:', error);
      throw new Error(error.response?.data?.error || 'Failed to start county trace');
    }
  },

  async traceCountyDryRun(county, limit = 25) {
    try {
      const response = await api.post('/trace/county', { county, limit, dryRun: true });
      return response.data;
    } catch (error) {
      console.error('Error starting county trace (dry run):', error);
      throw new Error(error.response?.data?.error || 'Failed to start county trace (dry run)');
    }
  },

  /**
   * Get Tracerfy queue status
   */
  async getQueue(queueId) {
    try {
      const response = await api.get(`/tracerfy/queue/${queueId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting queue status:', error);
      throw new Error(error.response?.data?.error || 'Failed to get queue status');
    }
  },

  async getTracerfyAnalytics() {
    try {
      const response = await api.get('/tracerfy/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching Tracerfy analytics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch analytics');
    }
  },

  /**
   * Fetch enriched rows for county from Supabase
   */
  async fetchEnrichedCounty(county, limit = 100) {
    try {
      const response = await api.get(`/supabase/enriched/${encodeURIComponent(county)}?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching enriched county:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch enriched rows');
    }
  },

  // In client/src/services/scrapingService.js
  async processBatchDataEnrichment(rawData, options = {}) {
    try {
      const response = await api.post('/batchdata/enrich', 
        { 
          rawData, 
          options: {
            ...options,
            saveToDatabase: true, // Tell server to save to database during processing
            filterMode: options.filterMode || 'between'
          } 
        }, 
        { timeout: 3200000 }
      );
      return response.data;
    } catch (error) {
      console.error('Error enriching batch data:', error);
      throw new Error(error.response?.data?.error || 'Failed to enrich data with BatchData');
    }
  },

  // Save enriched data to Supabase (unique_owner.enriched) (New function)
  async saveBatchDataEnrichment(enrichedData, countyId) {
    try {
      const response = await api.post('/supabase/unique-owner/enriched', {
        enrichedData,
        countyId
      });
      return response.data;
    } catch (error) {
      console.error('Error saving enriched data:', error);
      throw new Error(error.response?.data?.error || 'Failed to save enriched data');
    }
  },
  // =======================================
  // Newly added functions
  // =======================================
  // ALL rows in one go with filtering (Updated)
  async fetchUniqueOwnerEnrichedAll(countyId, minAcres = 0, maxAcres = null, filterMode = 'between') {
    try {
      const params = new URLSearchParams({ 
        countyId: String(countyId),
        all: 'true',
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/enriched?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ALL unique owner enriched:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch ALL enriched records');
    }
  },

  // Fetch count of unique owners in non-enriched table for a county (New function)
  async fetchUniqueOwnerNonEnrichedCount(countyId) {
    try {
        const response = await api.get(`/supabase/unique-owner/non-enriched/count?countyId=${countyId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching unique owner non-enriched count:', error);
        throw new Error(error.response?.data?.error || 'Failed to fetch count');
      }
  },

  // Fetch count of unique owners in enriched table for a county (New function)
  async fetchUniqueOwnerEnrichedCount(countyId) {
    try {
      const response = await api.get(`/supabase/unique-owner/enriched/count?countyId=${countyId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unique owner enriched count:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch enriched count');
    }
  },
  
  // Verify emails with ZeroBounce API
  async verifyEmailsWithZeroBounce(enrichedRecords, countyId) {
    try {
      // Send ALL records with emails - backend handles per-slot deduplication
      // The zerobounce_verified flag on owner may be true even if some slots are unverified
      const recordsToVerify = enrichedRecords.filter(
        record => record.email_1 || record.email_2 || record.email_3 || record.email_4
      );
      
      if (recordsToVerify.length === 0) {
        return {
          success: true,
          message: 'No records with emails to verify',
          verifiedCount: 0,
          records: enrichedRecords
        };
      }
      
      console.log(`Sending ${recordsToVerify.length} records to ZeroBounce (backend will skip already-verified slots)...`);
      
      const response = await api.post('/email-verification/verify-batch', {
        records: recordsToVerify,
        countyId,
        options: {
          skipAlreadyVerified: true,
          updateLegacyFields: true
        }
      }, { 
        timeout: 1200000 // 20-minute timeout for large batches
      });
      
      return response.data;
    } catch (error) {
      console.error('Error verifying emails with ZeroBounce:', error);
      throw new Error(error.response?.data?.error || 'Failed to verify emails with ZeroBounce');
    }
  },

  // Fetch ZeroBounce verification results for owners
  async fetchEmailVerificationResults(ownerIds) {
    try {
      const idsParam = Array.isArray(ownerIds) ? ownerIds.join(',') : ownerIds;
      const response = await api.get(`/email-verification/results?ownerIds=${encodeURIComponent(idsParam)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching email verification results:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch verification results');
    }
  },

  // Check email verification API status
  async checkEmailVerificationStatus() {
    try {
      const response = await api.get('/email-verification/status');
      return response.data;
    } catch (error) {
      console.error('Error checking email verification status:', error);
      return { success: false, configured: false };
    }
  },

  // =======================================
  // Auth methods
  // =======================================
  /**
   * Login with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} Authentication result
   */
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password });
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  },

  /**
   * Check if the user has a valid session
   * @returns {Promise<Object>} Session status
   */
  async checkSession() {
    try {
      const response = await api.get('/auth/session');
      return response.data;
    } catch (error) {
      console.error('Session check error:', error);
      return { authenticated: false };
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<Object>} Logout result
   */
  async logout() {
    try {
      const response = await api.post('/auth/logout');
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error(error.response?.data?.error || 'Logout failed');
    }
  },

  // NEW: Fetch filtered count of unique owners in non-enriched table
  async fetchFilteredUniqueOwnerNonEnrichedCount(countyId, minAcres, maxAcres, filterMode = 'between') {
    try {
      const params = new URLSearchParams({
        countyId: String(countyId),
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/non-enriched/filtered-count?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered non-enriched count:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch filtered count');
    }
  },

  // NEW: Fetch filtered count of unique owners in enriched table
  async fetchFilteredUniqueOwnerEnrichedCount(countyId, minAcres, maxAcres, filterMode = 'between') {
    try {
      const params = new URLSearchParams({
        countyId: String(countyId),
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/enriched/filtered-count?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered enriched count:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch filtered enriched count');
    }
  },

  // NEW: Fetch count of unverified (zerobounce_verified=false) enriched records
  async fetchUnverifiedEnrichedCount(countyId, minAcres, maxAcres, filterMode = 'between') {
    try {
      const params = new URLSearchParams({
        countyId: String(countyId),
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/enriched/unverified-count?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unverified count:', error);
      // Return 0 if endpoint doesn't exist yet (graceful degradation)
      return { unverified_count: 0 };
    }
  },

  // NEW: Fetch email stats for enriched records
  async fetchEnrichedEmailStats(countyId, minAcres, maxAcres, filterMode = 'between') {
    try {
      const params = new URLSearchParams({
        countyId: String(countyId),
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/enriched/email-stats?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching email stats:', error);
      // Return basic structure if endpoint doesn't exist yet
      return { total_emails: 0, verified_emails: 0, unverified_rows: 0 };
    }
  },

  // NEW: Fetch comprehensive KPI stats for cost analytics dashboard
  async fetchKpiStats(countyId, minAcres, maxAcres, filterMode = 'between') {
    try {
      const params = new URLSearchParams({
        countyId: String(countyId),
        filterMode: filterMode
      });
      
      if (minAcres) {
        params.append('minAcres', String(minAcres));
      }
      if (maxAcres) {
        params.append('maxAcres', String(maxAcres));
      }
      
      const response = await api.get(`/supabase/unique-owner/kpi-stats?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching KPI stats:', error);
      // Return basic structure if endpoint doesn't exist yet
      return {
        nonEnrichedCount: 0,
        enrichedCount: 0,
        enrichedWithEmail: 0,
        enrichedWithoutEmail: 0,
        totalEmailSlots: 0,
        verifiedEmails: 0,
        unverifiedEmails: 0,
        zbStatusBreakdown: {
          valid: 0,
          invalid: 0,
          catch_all: 0,
          unknown: 0,
          spamtrap: 0,
          abuse: 0,
          do_not_mail: 0,
          other: 0
        },
        validEmailRate: 0,
        enrichmentSuccessRate: 0
      };
    }
  },

  // =======================================
  // BatchData V3 weak-match recovery
  // =======================================

  async fetchBatchDataV3Preflight() {
    try {
      const response = await api.get('/batchdata/v3/preflight');
      return response.data;
    } catch (error) {
      console.error('Error fetching V3 preflight:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch V3 preflight');
    }
  },

  /**
   * payload: { countyId, limit, dryRun, mock, confirmLive, includeEntities,
   *            excludeProcessed, minAcres, maxAcres, filterMode }
   * Live runs require dryRun=false, mock=false, confirmLive=true.
   */
  async runBatchDataV3WeakMatchRecovery(payload) {
    try {
      const response = await api.post('/batchdata/v3/recover-weak-matches', payload, {
        timeout: 1800000, // 30 min: batches of up to 25 owners with retries
      });
      return response.data;
    } catch (error) {
      console.error('Error running V3 weak-match recovery:', error);
      throw new Error(error.response?.data?.error || 'Failed to run V3 weak-match recovery');
    }
  },

  async fetchBatchDataV3Summary(countyId) {
    try {
      const response = await api.get(`/batchdata/v3/summary?countyId=${encodeURIComponent(countyId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching V3 summary:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch V3 summary');
    }
  },

  /**
   * Before/after recovery comparison metrics, optionally acreage-filtered.
   * params: { countyId, filterMode, minAcres, maxAcres }
   */
  async fetchBatchDataV3RecoveryMetrics(params) {
    try {
      const qs = new URLSearchParams();
      qs.set('countyId', params.countyId);
      if (params.filterMode) qs.set('filterMode', params.filterMode);
      if (params.minAcres != null) qs.set('minAcres', params.minAcres);
      if (params.maxAcres != null) qs.set('maxAcres', params.maxAcres);
      const response = await api.get(`/batchdata/v3/recovery-metrics?${qs.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching V3 recovery metrics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch V3 recovery metrics');
    }
  },

  async fetchBatchDataV3OwnerContacts(uniqueId) {
    try {
      const response = await api.get(`/batchdata/v3/owner/${encodeURIComponent(uniqueId)}/contacts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching V3 owner contacts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch V3 owner contacts');
    }
  },

  /**
   * Downloads the V3 final export CSV for a county.
   * shape: 'leads' (one row per selected email, Leads-tab columns + name-match
   * columns), 'owner' (one row per CAD owner) or 'contact' (one row per person).
   * filters: optional { filterMode, minAcres, maxAcres } acreage restriction.
   */
  async downloadBatchDataV3FinalCsv(countyId, shape = 'leads', filters = {}) {
    try {
      const params = { countyId, shape };
      if (filters.filterMode) params.filterMode = filters.filterMode;
      if (filters.minAcres != null) params.minAcres = filters.minAcres;
      if (filters.maxAcres != null) params.maxAcres = filters.maxAcres;
      const response = await api.get('/batchdata/v3/export', {
        params,
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batchdata_v3_${shape}_export_county${countyId}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error downloading V3 export CSV:', error);
      throw new Error(error.response?.data?.error || 'Failed to download V3 export CSV');
    }
  },

  // =======================================
  // Phase 7: BatchData V3 Leads workflow (Phase 2/4 preview+jobs) +
  // provider-neutral effective Leads (Phase 6). Kept fully separate from
  // processBatchDataEnrichment()/the legacy /api/batchdata/enrich workflow —
  // never overload the old method to secretly mean V3.
  // =======================================

  /**
   * Phase 2 preview — READ-ONLY, never creates a job or spends credits.
   * options: { countyIds, filterMode, minAcres, maxAcres, includeEntities, forceReprocess }
   */
  async previewV3LeadsEnrichment(options) {
    try {
      const response = await api.post('/batchdata/v3/leads/preview', options);
      return response.data;
    } catch (error) {
      console.error('Error previewing V3 Leads enrichment:', error);
      throw new Error(error.response?.data?.error || 'Failed to preview V3 Leads enrichment');
    }
  },

  async previewPendingEffectiveEmails(scope) {
    try {
      const { data } = await api.post('/batchdata/v3/leads/zerobounce-only/preview', scope);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to preview pending emails');
    }
  },

  async createPendingEffectiveEmailJob(scope) {
    try {
      const { data } = await api.post('/batchdata/v3/leads/zerobounce-only/scope-jobs', scope);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create verification job');
    }
  },

  /**
   * Phase 4 job creation. Requires the caller to have already shown the user
   * an explicit confirmation — confirmLive:true must only ever be set as a
   * direct result of that user action, never automatically after preview.
   * options: { countyIds, filterMode, minAcres, maxAcres, includeEntities,
   *            forceReprocess, zerobounceEnabled, verifyRemainingEffectiveEmails, confirmLive }
   */
  async createV3LeadsJob(options) {
    try {
      const response = await api.post('/batchdata/v3/leads/jobs', options);
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      const message = typeof data?.error === 'string' && data.error.trim()
        ? data.error : 'Failed to create V3 Leads job';
      const wrapped = new Error(message);
      wrapped.status = error.response?.status;
      if (error.response) {
        // Retain the actionable API contract without copying request headers,
        // provider details, raw Axios errors or unrelated response fields.
        wrapped.response = {
          status: error.response.status,
          data: { error: message, ...(typeof data?.code === 'string' ? { code: data.code } : {}) },
        };
      }
      throw wrapped;
    }
  },

  async getV3LeadsJob(jobId) {
    try {
      const response = await api.get(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}`);
      return response.data;
    } catch (error) {
      // Preserve status so callers can distinguish 404 (stale job) from other errors.
      throw recoveryError(error, 'Failed to fetch V3 Leads job');
    }
  },

  async retryV3UnresolvedEmails(jobId, confirmation) {
    try {
      const response = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/retry-unresolved-emails`, {
        confirmDuplicateChargeRisk: confirmation === true,
      });
      return response.data;
    } catch (error) { throw new Error(error.response?.data?.error || 'Unable to retry unresolved emails'); }
  },

  async cancelV3LeadsJob(jobId) {
    try {
      const response = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling V3 Leads job:', error);
      throw new Error(error.response?.data?.error || 'Failed to cancel V3 Leads job');
    }
  },

  /** Ordinary safe retry ONLY — never for retry_confirmation_required items. */
  async retryV3LeadsJobFailures(jobId) {
    try {
      const response = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/retry-failed`);
      return response.data;
    } catch (error) {
      console.error('Error retrying V3 Leads job failures:', error);
      throw new Error(error.response?.data?.error || 'Failed to retry V3 Leads job failures');
    }
  },

  /**
   * Uncertain-retry confirmation — must only be called after an explicit,
   * distinct second confirmation from the user (never automatic).
   * confirmation must be exactly true; itemIds is the specific
   * retry_confirmation_required job_item_id list to requeue.
   */
  async confirmV3LeadsUncertainRetry(jobId, itemIds, confirmation, operationId) {
    try {
      const response = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/confirm-uncertain-retry`, {
        jobItemIds: itemIds,
        confirmDuplicateChargeRisk: confirmation === true,
        operationId,
      });
      return response.data;
    } catch (error) {
      throw recoveryError(error, 'Failed to confirm V3 Leads uncertain retry');
    }
  },

  async reviewV3LeadsRecovery(scope) {
    try {
      const { data } = await api.post('/batchdata/v3/leads/recovery-review', scope);
      return data;
    } catch (error) { throw recoveryError(error, 'Unable to review uncertain BatchData attempts'); }
  },

  async reconcileV3LeadsUncertain(jobId, options) {
    try {
      const { data } = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/reconcile-uncertain`, {
        jobItemIds: options.jobItemIds, operationId: options.operationId,
        dryRun: options.dryRun !== false, confirmReconciliation: options.confirmReconciliation === true,
      });
      return data;
    } catch (error) { throw recoveryError(error, 'Unable to reconcile interrupted BatchData attempts'); }
  },

  async resumeV3LeadsDispatch(jobId, options) {
    try {
      const { data } = await api.post(`/batchdata/v3/leads/jobs/${encodeURIComponent(jobId)}/resume-dispatch`, {
        operationId: options.operationId, confirmResume: options.confirmResume === true,
      });
      return data;
    } catch (error) { throw recoveryError(error, 'Unable to resume paused BatchData dispatch'); }
  },

  /**
   * Phase 6 provider-neutral effective Leads rows (one row per email).
   * options: { countyIds, filterMode, minAcres, maxAcres, limit, offset }
   */
  async fetchEffectiveLeads(options, { signal } = {}) {
    try {
      const response = await api.get('/leads/effective', { params: options, signal });
      return response.data;
    } catch (error) {
      if (signal?.aborted || axios.isCancel(error)) {
        const cancelled = new Error('Loading cancelled. Results are incomplete.');
        cancelled.name = 'AbortError';
        throw cancelled;
      }
      const status = error.response?.status;
      const message = status === 401 || status === 403 ? 'Authentication required. Sign in and reload leads.'
        : status === 429 ? 'Rate limit reached. Wait before reloading leads.'
          : 'Failed to load Effective Leads. Results are incomplete; retry the load.';
      throw new Error(message);
    }
  },

  async fetchAllEffectiveLeads(options, controls = {}) {
    return loadAllEffectiveLeads((page, request) => scrapingService.fetchEffectiveLeads(page, request), options, controls);
  },

// =======================================  // End Auth methods
};

export default scrapingService;
